// src/components/admin/AchievementDetailsModal.tsx
import React, { useState, useEffect } from 'react';
import { XMarkIcon, HomeIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { getBungieIconUrl } from '../../services/bungieIconService';
import { getAchievementDetails, Achievement, getGroupedAchievements, Group, Category } from '../../services/achievementService';
import { getStoredLanguage } from '../../services/languageService';
import LoadingState from '../common/LoadingState';

interface AchievementDetailsModalProps {
  achievementId: number;
  isOpen: boolean;
  onClose: () => void;
}

// Define the hierarchy path for an achievement
interface HierarchyPath {
  groupName: string;
  groupId: number;
  categoryPath: {
    id: number;
    name: string;
  }[];
}

const AchievementDetailsModal: React.FC<AchievementDetailsModalProps> = ({
  achievementId,
  isOpen,
  onClose
}) => {
  const [achievement, setAchievement] = useState<Achievement | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [language, setLanguage] = useState<string>(getStoredLanguage());
  const [hierarchyPath, setHierarchyPath] = useState<HierarchyPath | null>(null);
  
  // Helper function to find an achievement in the hierarchy and build its path
  const findAchievementPath = (achievementId: number, groups: Group[]): HierarchyPath | null => {
    // For each group, check if the achievement exists in it
    for (const group of groups) {
      // Recursive function to search categories and their subcategories
      const findInCategories = (categories: Category[], currentPath: {id: number, name: string}[] = []): {id: number, name: string}[] | null => {
        for (const category of categories) {
          // Check if this category contains the achievement
          if (category.achievements && category.achievements.some(a => a.id === achievementId)) {
            return [...currentPath, { id: category.id, name: category.display_name }];
          }
          
          // Check subcategories if they exist
          if (category.subcategories && category.subcategories.length > 0) {
            const subPath = findInCategories(
              category.subcategories, 
              [...currentPath, { id: category.id, name: category.display_name }]
            );
            if (subPath) return subPath;
          }
        }
        return null;
      };
      
      // Start the search in this group's categories
      const categoryPath = findInCategories(group.categories);
      
      // If we found the achievement in this group, return the path
      if (categoryPath) {
        return {
          groupName: group.display_name,
          groupId: group.id,
          categoryPath
        };
      }
    }
    
    // Achievement not found in any group
    return null;
  };

  useEffect(() => {
    const fetchAchievementDetails = async () => {
      if (!achievementId) return;
      
      setLoading(true);
      setError(null);
      setHierarchyPath(null);
      
      try {
        // Fetch achievement details
        const data = await getAchievementDetails(achievementId);
        setAchievement(data);
        
        // Also fetch the groups structure to find the path
        try {
          const groups = await getGroupedAchievements();
          const path = findAchievementPath(achievementId, groups);
          setHierarchyPath(path);
        } catch (pathErr) {
          console.error('Error finding achievement path:', pathErr);
          // We don't set an error here because the main achievement data was loaded successfully
        }
      } catch (err) {
        console.error('Error fetching achievement details:', err);
        setError('Failed to load achievement details');
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen) {
      fetchAchievementDetails();
    }
  }, [achievementId, isOpen]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-90 flex items-center justify-center p-4">
      <div className="relative w-full max-w-3xl bg-gray-800 rounded-lg shadow-xl">
        <div className="flex justify-between items-center p-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-destiny-gold">Achievement Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <div className="p-6 max-h-[80vh] overflow-y-auto">
          {loading ? (
            <LoadingState message="Loading achievement details..." />
          ) : error ? (
            <div className="bg-destiny-red bg-opacity-20 border border-destiny-red rounded-lg p-4">
              <p className="text-destiny-red">{error}</p>
            </div>
          ) : achievement ? (
            <div>
                  {/* Achievement Hierarchy/Breadcrumb Path */}
              {hierarchyPath && (
                <div className="bg-gray-700 rounded-lg p-3 mb-4 flex items-center text-sm overflow-x-auto">
                  <div className="flex items-center text-gray-400 min-w-max">
                    <HomeIcon className="h-4 w-4 mr-1" />
                    <span>{hierarchyPath.groupName}</span>
                  </div>
                  
                  {hierarchyPath.categoryPath.map((category, index) => (
                    <div key={category.id} className="flex items-center min-w-max">
                      <ChevronRightIcon className="h-4 w-4 mx-1 text-gray-500" />
                      <span className={index === hierarchyPath.categoryPath.length - 1 ? "text-destiny-gold" : "text-gray-400"}>
                        {category.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex items-start mb-6">
                {achievement.icon_url ? (
                  <img
                    src={getBungieIconUrl(achievement.icon_url)}
                    alt={achievement.name}
                    className="w-16 h-16 rounded-md mr-4 bg-gray-700"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjMzc0MTUxIiByeD0iNCIgLz48L3N2Zz4=';
                    }}
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-700 rounded-md mr-4 flex items-center justify-center text-white font-bold text-lg">
                    {achievement.name.charAt(0)}
                  </div>
                )}
                <div>
                  <h3 className="text-xl font-bold text-white">{achievement.name}</h3>
                  {achievement.category_name && (
                    <span className="inline-block bg-gray-700 px-2 py-1 rounded-full text-xs font-medium text-gray-300 mt-1">
                      {achievement.category_name}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="text-destiny-gold font-medium mb-2">Description</h4>
                <p className="text-gray-300">{achievement.description}</p>
              </div>
              
              {/* Show points and other details */}
              <div className="mb-6">
                <h4 className="text-destiny-gold font-medium mb-2">Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-700 p-3 rounded">
                    <span className="text-gray-400 text-sm">Points:</span>
                    <p className="text-white">{achievement.points}</p>
                  </div>
                  <div className="bg-gray-700 p-3 rounded">
                    <span className="text-gray-400 text-sm">Record Hash:</span>
                    <p className="text-white font-mono text-sm break-all">{achievement.record_hash}</p>
                  </div>
                  
                  <div className="bg-gray-700 p-3 rounded">
                    <span className="text-gray-400 text-sm">ID:</span>
                    <p className="text-white">{achievement.id}</p>
                  </div>
                  
                  {hierarchyPath && (
                    <div className="bg-gray-700 p-3 rounded">
                      <span className="text-gray-400 text-sm">Group:</span>
                      <p className="text-white">{hierarchyPath.groupName}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Show objectives/steps if available */}
              {achievement.steps && achievement.steps.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-destiny-gold font-medium mb-2">Objectives</h4>
                  <div className="space-y-2">
                    {achievement.steps.map((step, index) => (
                      <div key={step.id || index} className="bg-gray-700 p-3 rounded">
                        <p className="text-white">{step.name || `Step ${index + 1}`}</p>
                        {step.description && (
                          <p className="text-gray-400 text-sm mt-1">{step.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              Achievement not found
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AchievementDetailsModal;